/**
 * RevisionHistory Component
 *
 * Displays revision history for a note audit
 */

import React from 'react';
import { Alert, Box, Paper, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useRevisionHistory } from '../../hooks/useRevisionHistory';

import { RevisionTimeline } from './RevisionTimeline';

interface RevisionHistoryProps {
  /** Note ID to fetch history for */
  noteId: string | undefined;
  /** Currently selected revision number */
  selectedRevision?: number;
  /** Callback when selecting a revision */
  onSelectRevision?: (revision: number) => void;
}

export const RevisionHistory: React.FC<RevisionHistoryProps> = ({
  noteId,
  selectedRevision,
  onSelectRevision,
}) => {
  const theme = useTheme();
  const { revisions, isLoading, error } = useRevisionHistory(noteId);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          History
        </Typography>
        {!isLoading && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {revisions.length} {revisions.length === 1 ? 'revision' : 'revisions'}
          </Typography>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map(i => (
              <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="circular" width={12} height={12} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" height={20} sx={{ mb: 0.5 }} />
                  <Skeleton width="40%" height={16} sx={{ mb: 0.5 }} />
                  <Skeleton width="80%" height={16} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <RevisionTimeline
            revisions={revisions}
            selectedRevision={selectedRevision}
            onSelectRevision={onSelectRevision}
          />
        )}
      </Box>
    </Paper>
  );
};

export default RevisionHistory;

